import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import { fetchSiteSettings } from '../../services/siteSettingsService';


const RoleBasedNavigation = ({ notifications = [] }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [horizontalLogo, setHorizontalLogo] = useState(null);
  const [showLogo, setShowLogo] = useState(false);
  const [showSignUpButton, setShowSignUpButton] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpenSection, setMobileOpenSection] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // CRITICAL FIX: Use ONLY user from AuthContext, not from props
  const { user, userProfile, signOut, isAuthenticated } = useAuth();

  useEffect(() => {
    // Fetch site logos
    const loadLogos = async () => {
      const { horizontalLogo: logo } = await fetchSiteSettings();
      setHorizontalLogo(logo);
    };

    loadLogos();
  }, []);

  // Check if user is on landing page
  const isLandingPage = location?.pathname === '/' || location?.pathname === '/landing-page';

  // Check if user is on auth pages
  const isLoginPage = location?.pathname === '/login';
  const isRegistrationPage = location?.pathname === '/registration';

  // Add scroll detection for logo visibility on landing page
  useEffect(() => {
    if (!isLandingPage) {
      setShowLogo(true); // Always show logo on non-landing pages
      setShowSignUpButton(true); // Always show sign-up button on non-landing pages
      return;
    }

    // Hide logo initially on landing page
    setShowLogo(false);
    setShowSignUpButton(true);

    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      // Show logo after scrolling 450px (past hero section)
      if (scrollY > 450) {
        setShowLogo(true);
        setShowSignUpButton(false); // Hide sign-up button when past hero
      } else {
        setShowLogo(false);
        setShowSignUpButton(true); // Show sign-up button in hero section
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isLandingPage]);

  // Landing page navigation items
  const landingPageNavItems = [
    { label: 'Benefits', path: '/#benefits', section: 'benefits' },
    { label: 'Features', path: '/#features', section: 'features' },
    { label: 'Plays', path: '/#plays', section: 'plays' },
    { label: 'Testimonials', path: '/#testimonials', section: 'testimonials' },
    { label: 'About', path: '/#founders', section: 'founders' }
  ];

  // Student dashboard navigation items - shown between logo and user profile
  const studentHeaderNav = [
    { label: 'Stage', path: '/student-stage-dashboard', icon: 'Theater' },
    { label: 'Plays', path: '/student-plays-library', icon: 'BookText' },
    { label: 'Glossary', path: '/student-glossary-explorer', icon: 'BookOpen' },
    { label: 'Progress', path: '/student-progress-center', icon: 'TrendingUp' }
  ];

  // Admin dashboard navigation items - shown between logo and user profile
  const adminHeaderNav = [
    { 
      label: 'Content', 
      icon: 'BookOpen',
      dropdown: [
        { label: 'Plays', path: '/play-management', icon: 'Theater' },
        { label: 'Characters', path: '/characters-library', icon: 'Users2' },
        { label: 'Glossary', path: '/glossary-management', icon: 'BookText' },
        { label: 'Audio Production', path: '/audio-production-dashboard', icon: 'Music' },
        { label: 'Audio Validation', path: '/audio-quality-validation-dashboard', icon: 'CheckCircle2' },
        { label: 'Voice Actor Performance', path: '/voice-actor-performance-analytics', icon: 'BarChart3' },
        { label: 'Badge Management', path: '/badge-management', icon: 'Award' },
        { label: 'Achievement Management', path: '/achievement-management', icon: 'Trophy' }
      ]
    },
    { 
      label: 'Analytics', 
      icon: 'BarChart3',
      dropdown: [
        { label: 'General Analytics', path: '/analytics-dashboard', icon: 'BarChart3' },
        { label: 'Badge & Achievement Analytics', path: '/badge-achievement-analytics', icon: 'Award' },
        { label: 'Pronunciation Analytics', path: '/pronunciation-analytics-dashboard', icon: 'Mic' },
      ]
    },
    { 
      label: 'Settings', 
      icon: 'Settings',
      dropdown: [
        { label: 'Users', path: '/user-management', icon: 'Users' },
        { label: 'Site Settings', path: '/site-settings', icon: 'Settings' },
      ]
    },
    { 
      label: 'Student Stage', 
      path: '/student-stage-dashboard', 
      icon: 'GraduationCap'
    }
  ];

  // Voice Actor dashboard navigation items
  const voiceActorHeaderNav = [
    { label: 'Portal', path: '/voice-actor-portal-dashboard', icon: 'Home' },
    { label: 'Assignments', path: '/voice-actor-assignment-interface', icon: 'Briefcase' },
    { label: 'Performance', path: '/voice-actor-performance-analytics', icon: 'BarChart3' },
    { label: 'Audio Production', path: '/audio-production-dashboard', icon: 'Music' },
    { label: 'Trending Feed', path: '/trending-performers-feed', icon: 'TrendingUp' }
  ];

  const getNavigationItems = () => {
    if (!isAuthenticated) {
      return [
        { label: 'Home', path: '/landing-page', icon: 'Home', roles: ['public'] },
        { label: 'Login', path: '/login', icon: 'LogIn', roles: ['public'] },
        { label: 'Register', path: '/registration', icon: 'UserPlus', roles: ['public'] }
      ];
    }

    // Use userProfile.role with proper fallback
    const userRole = userProfile?.role || 'student';

    const navigationMap = {
      student: [
        { label: 'Stage', path: '/student-stage-dashboard', icon: 'Theater', roles: ['student'] },
        { label: 'Quiz', path: '/quiz-interface', icon: 'BookOpen', roles: ['student'] },
        { label: 'Reading', path: '/play-reading-interface', icon: 'Book', roles: ['student'] }
      ],
      admin: [
        { label: 'Dashboard', path: '/admin-dashboard', icon: 'LayoutDashboard', roles: ['admin', 'superadmin'] },
        { label: 'Plays', path: '/play-management', icon: 'Theater', roles: ['admin', 'superadmin'] },
        { label: 'Glossary', path: '/glossary-management', icon: 'BookText', roles: ['admin', 'superadmin'] },
        { label: 'Users', path: '/user-management', icon: 'Users', roles: ['admin', 'superadmin'] },
        { label: 'Analytics', path: '/analytics-dashboard', icon: 'BarChart3', roles: ['admin', 'superadmin'] }
      ],
      superadmin: [
        { label: 'Dashboard', path: '/admin-dashboard', icon: 'LayoutDashboard', roles: ['admin', 'superadmin'] },
        { label: 'Plays', path: '/play-management', icon: 'Theater', roles: ['admin', 'superadmin'] },
        { label: 'Glossary', path: '/glossary-management', icon: 'BookText', roles: ['admin', 'superadmin'] },
        { label: 'Users', path: '/user-management', icon: 'Users', roles: ['admin', 'superadmin'] },
        { label: 'Analytics', path: '/analytics-dashboard', icon: 'BarChart3', roles: ['admin', 'superadmin'] },
        { label: 'Settings', path: '/site-settings', icon: 'Settings', roles: ['superadmin'] }
      ]
    };

    return navigationMap?.[userRole] || navigationMap?.student;
  };

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    
    // Call the actual signOut method from AuthContext
    const { error } = await signOut();
    
    if (error) {
      console.error('Logout error:', error);
      // Still navigate even if error occurs
    }
    
    // Navigate to login page after logout
    navigate('/login');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navigationItems = getNavigationItems();
  const unreadNotifications = notifications?.filter(n => !n?.read)?.length;

  // Get user initials for avatar
  const getUserInitials = () => {
    const fullName = userProfile?.full_name || user?.email;
    if (!fullName) return 'U';
    const names = fullName?.split(' ');
    if (names?.length >= 2) {
      return `${names?.[0]?.[0]}${names?.[1]?.[0]}`?.toUpperCase();
    }
    return fullName?.[0]?.toUpperCase() || 'U';
  };

  // Get header navigation based on user role
  const getHeaderNavigation = () => {
    if (!isAuthenticated) return [];
    const userRole = userProfile?.role || 'student';
    const isVoiceActor = userProfile?.is_voice_actor === true;
    
    // Voice actors get their own navigation
    if (isVoiceActor) return voiceActorHeaderNav;
    
    if (userRole === 'student') return studentHeaderNav;
    if (userRole === 'admin' || userRole === 'superadmin') return adminHeaderNav;
    return [];
  };

  const headerNavItems = getHeaderNavigation();

  // Check if user is admin or superadmin
  const isAdminUser = userProfile?.role === 'admin' || userProfile?.role === 'superadmin';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const handleDropdownToggle = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const handleDropdownItemClick = (path) => {
    setOpenDropdown(null);
    navigate(path);
  };

  // Navigation items for authenticated users
  const studentNavItems = [
    { label: 'Dashboard', path: '/student-stage-dashboard', icon: 'LayoutDashboard' },
    { label: 'Plays Library', path: '/student-plays-library', icon: 'BookOpen' },
    { label: 'Characters', path: '/characters-library', icon: 'Users2' },
    { label: 'Glossary', path: '/student-glossary-explorer', icon: 'BookMarked' },
    { label: 'Progress', path: '/student-progress-center', icon: 'TrendingUp' },
    { label: 'Goals', path: '/student-goal-setting-center', icon: 'Target' },
    { label: 'Performers', path: '/trending-performers-feed', icon: 'Mic2' },
  ];

  const publicMenuItems = [
    { label: 'Features', path: '/features' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'For Students', path: '/for-students' },
    { label: 'Characters', path: '/characters-library' },
    { label: 'Network Visualization', path: '/character-network-visualization' },
    { label: 'Help Center', path: '/help-center' },
    { label: 'More Shakespeare', path: '/more-shakespeare' },
  ];

  return (
    <>
      <header className="header-nav">
        <div className="header-container">
          {/* Logo */}
          <Link to="/" className="header-logo">
            {horizontalLogo && showLogo && (
              <img 
                src={horizontalLogo} 
                alt="BardPlay horizontal logo" 
                className="h-8 w-auto mr-2 transition-opacity duration-300"
              />
            )}
            <span className="header-logo-text">BardPlay</span>
          </Link>

          {/* Desktop Navigation - Show landing page nav when not authenticated */}
          {!isAuthenticated && isLandingPage && (
            <nav className="hidden md:flex items-center gap-6 mx-auto">
              {landingPageNavItems?.map((item) => (
                item?.section ? (
                  <HashLink
                    key={item?.section}
                    to={item?.path}
                    smooth
                    className="text-text-secondary hover:text-white transition-colors text-sm font-medium"
                  >
                    {item?.label}
                  </HashLink>
                ) : (
                  <Link
                    key={item?.label}
                    to={item?.path}
                    className="text-text-secondary hover:text-white transition-colors text-sm font-medium"
                  >
                    {item?.label}
                  </Link>
                )
              ))}
            </nav>
          )}

          {/* Authenticated User Navigation - Show role-based nav between logo and user profile */}
          {isAuthenticated && headerNavItems?.length > 0 && (
            <nav className="hidden md:flex items-center gap-4 mx-auto" ref={dropdownRef}>
              {headerNavItems?.map((item) => (
                item?.dropdown ? (
                  <div key={item?.label} className="relative">
                    <button
                      onClick={() => handleDropdownToggle(item?.label)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                        item?.dropdown?.some(subItem => location?.pathname === subItem?.path)
                          ? 'bg-accent-purple/20 text-accent-purple font-semibold' :'text-text-secondary hover:text-white hover:bg-surface-lighter'
                      }`}
                    >
                      <Icon name={item?.icon} size={18} />
                      <span className="text-sm">{item?.label}</span>
                      <Icon name={openDropdown === item?.label ? 'ChevronUp' : 'ChevronDown'} size={16} />
                    </button>
                    {openDropdown === item?.label && (
                      <div className="absolute top-full left-0 mt-1 bg-[#3D0066] border border-border rounded-lg shadow-lg py-2 min-w-[200px] z-50">
                        {item?.dropdown?.map((subItem) => (
                          <button
                            key={subItem?.path}
                            onClick={() => handleDropdownItemClick(subItem?.path)}
                            className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                              location?.pathname === subItem?.path
                                ? 'bg-accent-purple/20 text-accent-purple font-semibold' :'text-text-secondary hover:text-white hover:bg-surface-lighter'
                            }`}
                          >
                            <Icon name={subItem?.icon} size={16} />
                            <span>{subItem?.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      location?.pathname === item?.path
                        ? 'bg-accent-purple/20 text-accent-purple font-semibold' :'text-text-secondary hover:text-white hover:bg-surface-lighter'
                    }`}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span className="text-sm">{item?.label}</span>
                  </Link>
                )
              ))}
            </nav>
          )}

          {/* Action Buttons / User Profile */}
          <div className="header-actions flex items-center gap-3">
            {!isAuthenticated ? (
              <>
                {!isLoginPage && (
                  <Link to="/login">
                    <button className="btn-secondary text-sm px-6 py-2.5">
                      Sign In
                    </button>
                  </Link>
                )}
                {showSignUpButton && !isRegistrationPage && (
                  <Link to="/registration">
                    <button className="btn-primary text-sm px-6 py-2.5">
                      Sign Up
                    </button>
                  </Link>
                )}
                
                {/* Mobile Toggle for non-authenticated users */}
                <button
                  className="header-mobile-toggle md:hidden ml-2"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label="Toggle mobile menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
                </button>
              </>
            ) : (
              <>
                {/* Desktop User Profile Dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-surface-dark/50 transition-colors"
                    aria-label="User menu"
                    aria-expanded={isUserMenuOpen}
                  >
                    {userProfile?.profile_photo_url ? (
                      <img 
                        src={userProfile?.profile_photo_url} 
                        alt={`${userProfile?.full_name || 'User'} profile photo`}
                        className="w-10 h-10 rounded-full object-cover shadow-lg border-2 border-accent-purple/30"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center text-white font-semibold shadow-lg">
                        {getUserInitials()}
                      </div>
                    )}
                    <Icon 
                      name={isUserMenuOpen ? 'ChevronUp' : 'ChevronDown'} 
                      size={20} 
                      className="text-text-secondary hidden sm:block" 
                    />
                  </button>

                  {/* Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-[#3D0066] border border-border rounded-xl shadow-2xl overflow-hidden z-50">
                      {/* User Info Section */}
                      <div className="px-4 py-3 border-b border-border">
                        <p className="font-semibold text-white text-sm">{userProfile?.full_name || 'User'}</p>
                        <p className="text-text-secondary text-xs truncate">{userProfile?.email || user?.email}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-accent-purple/20 text-accent-purple rounded-full capitalize">
                          {userProfile?.role || 'student'}
                        </span>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        {isAdminUser && (
                          <Link
                            to="/admin-dashboard"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-lighter transition-colors text-white"
                          >
                            <Icon name="LayoutDashboard" size={18} className="text-accent-purple" />
                            <span className="text-sm">Admin Dashboard</span>
                          </Link>
                        )}
                        
                        <Link
                          to="/settings"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-lighter transition-colors text-white"
                        >
                          <Icon name="Settings" size={18} className="text-text-secondary" />
                          <span className="text-sm">Settings</span>
                        </Link>

                        <div className="border-t border-border my-2"></div>

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-500/10 transition-colors text-red-400"
                        >
                          <Icon name="LogOut" size={18} />
                          <span className="text-sm">Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile Toggle for authenticated users - always visible on mobile */}
                <button
                  className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white/10 transition-colors text-white/70 hover:text-white"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label="Toggle mobile menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
          {/* Slide-out Drawer */}
          <nav
            className="fixed top-0 right-0 bottom-0 z-[1150] w-[85vw] max-w-[320px] overflow-y-auto md:hidden"
            style={{
              background: 'rgba(20, 10, 50, 0.98)',
              backdropFilter: 'blur(20px)',
              borderLeft: '1px solid rgba(139, 92, 246, 0.25)',
              animation: 'slideInRight 280ms ease-out',
            }}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 z-10" style={{ background: 'rgba(20, 10, 50, 0.98)' }}>
              <Link
                to={isAuthenticated ? (userProfile?.role === 'student' ? '/student-stage-dashboard' : '/admin-dashboard') : '/landing-page'}
                className="flex items-center gap-2 no-underline"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
                  <Icon name="Theater" size={18} color="white" />
                </div>
                <span className="text-white font-bold text-lg">BardPlay</span>
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors text-white/70 hover:text-white"
                aria-label="Close menu"
              >
                <Icon name="X" size={22} />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="px-3 py-4 flex flex-col gap-1">

              {/* Landing page nav (unauthenticated) */}
              {!isAuthenticated && isLandingPage && (
                landingPageNavItems?.map((item) => (
                  item?.section ? (
                    <HashLink
                      key={item?.section}
                      to={item?.path}
                      smooth
                      className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200 no-underline text-base font-medium active:bg-white/20"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span>{item?.label}</span>
                    </HashLink>
                  ) : (
                    <Link
                      key={item?.label}
                      to={item?.path}
                      className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200 no-underline text-base font-medium active:bg-white/20"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span>{item?.label}</span>
                    </Link>
                  )
                ))
              )}

              {/* Admin / authenticated header nav items with expandable dropdowns */}
              {isAuthenticated && headerNavItems?.length > 0 && (
                <>
                  {headerNavItems?.map((item) => (
                    item?.dropdown ? (
                      <div key={item?.label}>
                        {/* Section toggle button */}
                        <button
                          onClick={() => setMobileOpenSection(mobileOpenSection === item?.label ? null : item?.label)}
                          className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 text-base font-medium active:bg-white/20 ${
                            item?.dropdown?.some(sub => location?.pathname === sub?.path)
                              ? 'bg-accent-purple/20 text-accent-purple' :'text-white/80 hover:text-white hover:bg-white/10'
                          }`}
                          aria-expanded={mobileOpenSection === item?.label}
                        >
                          <span className="flex items-center gap-3">
                            <Icon name={item?.icon} size={20} />
                            <span>{item?.label}</span>
                          </span>
                          <Icon
                            name={mobileOpenSection === item?.label ? 'ChevronUp' : 'ChevronDown'}
                            size={18}
                            className="flex-shrink-0"
                          />
                        </button>
                        {/* Expanded sub-items */}
                        {mobileOpenSection === item?.label && (
                          <div className="ml-4 mt-1 flex flex-col gap-0.5 border-l-2 border-accent-purple/30 pl-3">
                            {item?.dropdown?.map((subItem) => (
                              <Link
                                key={subItem?.path}
                                to={subItem?.path}
                                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 no-underline text-sm font-medium active:bg-white/20 ${
                                  location?.pathname === subItem?.path
                                    ? 'bg-accent-purple/20 text-accent-purple font-semibold' :'text-white/70 hover:text-white hover:bg-white/10'
                                }`}
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                <Icon name={subItem?.icon} size={17} />
                                <span>{subItem?.label}</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 no-underline text-base font-medium active:bg-white/20 ${
                          location?.pathname === item?.path
                            ? 'bg-accent-purple/20 text-accent-purple font-semibold' :'text-white/80 hover:text-white hover:bg-white/10'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Icon name={item?.icon} size={20} />
                        <span>{item?.label}</span>
                      </Link>
                    )
                  ))}
                  <div className="my-2 border-t border-white/10" />
                </>
              )}

              {/* Non-authenticated non-landing nav items */}
              {!isAuthenticated && !isLandingPage && navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 no-underline text-base font-medium active:bg-white/20 ${
                    location?.pathname === item?.path
                      ? 'bg-accent-purple/20 text-accent-purple font-semibold' :'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </Link>
              ))}

              {/* Authenticated user account actions */}
              {isAuthenticated && (
                <>
                  {isAdminUser && (
                    <Link
                      to="/admin-dashboard"
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 no-underline text-base font-medium active:bg-white/20 ${
                        location?.pathname === '/admin-dashboard' ?'bg-accent-purple/20 text-accent-purple font-semibold' :'text-white/80 hover:text-white hover:bg-white/10'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon name="LayoutDashboard" size={20} />
                      <span>Admin Dashboard</span>
                    </Link>
                  )}

                  <Link
                    to="/settings"
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all duration-200 no-underline text-base font-medium active:bg-white/20"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon name="Settings" size={20} />
                    <span>Settings</span>
                  </Link>

                  <div className="my-2 border-t border-white/10" />

                  <button
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-200 text-base font-medium active:bg-red-500/20"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    <Icon name="LogOut" size={20} />
                    <span>Logout</span>
                  </button>
                </>
              )}

              {/* Sign in / Sign up for unauthenticated */}
              {!isAuthenticated && (
                <div className="mt-4 flex flex-col gap-3 px-1">
                  {!isLoginPage && (
                    <Link
                      to="/login"
                      className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl border border-white/20 text-white font-semibold text-base hover:bg-white/10 transition-all no-underline active:bg-white/20"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon name="LogIn" size={20} />
                      <span>Sign In</span>
                    </Link>
                  )}
                  {!isRegistrationPage && (
                    <Link
                      to="/registration"
                      className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-white font-semibold text-base transition-all no-underline active:scale-95"
                      style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)' }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon name="UserPlus" size={20} />
                      <span>Sign Up</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </nav>
        </>
      )}
    </>
  );
};

export default RoleBasedNavigation;